import { EntityId } from '@reduxjs/toolkit';
import { captureException } from 'fe-core/util/errorTracking';
import { RootState } from 'initializers/types';

import { PARENT_SLICE_NAME } from './constants';

export function captureHydrationException(
  state: any,
  error: any,
  sliceName: string
) {
  console.error('error!', error);
  captureException(error, { tags: { initiatve: 'fe-core' } });
  state.error = `Hydration failed for ${sliceName}`;
  state.status = 'failed';
}

export const selectAttributeForEntity = <
  TEntity,
  TAttribute extends keyof TEntity
>({
  state,
  attribute,
  entitySelector,
  id,
}: {
  state: RootState;
  attribute: TAttribute;
  entitySelector: (state: RootState, id: EntityId) => TEntity | undefined;
  id: EntityId;
}): TEntity[TAttribute] | undefined => {
  const foundEntity = entitySelector(state, id);
  return foundEntity ? foundEntity[attribute] : undefined;
};

export const getSliceState = (state: RootState) => {
  if (!state) {
    return {};
  }
  if (typeof state.get === 'function') {
    return state.get(PARENT_SLICE_NAME);
  }
  return (state as RootState)[PARENT_SLICE_NAME as keyof RootState];
};

export const getSliceStatus = (state: RootState, sliceName: string) =>
  getSliceState(state)?.[sliceName]?.status;

export const selectSliceAttribute = <T extends keyof any>({
  state,
  attribute,
  sliceName,
  initialState,
}: {
  state: RootState;
  attribute: T;
  sliceName: string;
  initialState?: any;
}): any[T] | undefined => {
  const sliceState = getSliceState(state)?.[sliceName] || initialState;
  return sliceState?.[attribute];
};

export const isSliceLoading = (state: RootState, sliceName: string) =>
  getSliceStatus(state, sliceName) === 'pending';

export const isSliceLoaded = (state: RootState, sliceName: string) =>
  getSliceStatus(state, sliceName) === 'succeeded';

export const isSliceFailed = (state: RootState, sliceName: string) =>
  getSliceStatus(state, sliceName) === 'failed';
